import { inject, NgModule } from '@angular/core';
import type { Routes } from '@angular/router';
import { RouterModule } from '@angular/router';
import { ENVIRONMENT_NAME } from '@freelancer/config';
import { BaseComponent } from './base/base.component';
import { EmptyComponent } from './base/empty.component';
import { NotFoundComponent } from './base/not-found/not-found.component';
import { BitsGuard } from './bits-guard.service';
import { componentRoutes } from './generated/component.routes';
import { directiveRoutes } from './generated/directive.routes';
import { patternRoutes } from './generated/patterns.routes';
import { pipeRoutes } from './generated/pipe.routes';
import { HomeComponent } from './home/home.component';

// WARNING: Non lazy-loaded components are stricly forbidden here: you MUST use
// loadChildren if you want to add a new top-level route.
export const appRoutes: Routes = [
  {
    path: '',
    component: HomeComponent,
  },
  {
    path: '',
    children: [
      {
        path: 'info',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: 'introduction',
            pathMatch: 'full',
          },
          {
            path: 'introduction',
            loadComponent: () =>
              import('./base/static-pages/about/about.component').then(
                c => c.AboutComponent,
              ),
          },
          {
            path: 'getting-started',
            loadComponent: () =>
              import(
                './base/static-pages/get-started/get-started.component'
              ).then(c => c.GetStartedComponent),
          },
          {
            path: 'faq',
            loadComponent: () =>
              import('./base/static-pages/faq/faq.component').then(
                c => c.FAQComponent,
              ),
          },
          {
            path: 'how-to-contribute',
            loadChildren: () =>
              import(
                './base/static-pages/how-to-contribute/how-to-contribute.routes'
              ).then(r => r.routes),
            canActivate: [BitsGuard],
          },
          {
            path: 'report-bugs',
            loadComponent: () =>
              import('./base/static-pages/bugs/bugs.component').then(
                c => c.BugsComponent,
              ),
            canActivate: [BitsGuard],
            data: {
              internalOnly: true,
            },
          },
          {
            path: 'modals',
            loadComponent: () =>
              import('./base/static-pages/modals/modals.component').then(
                c => c.ModalsComponent,
              ),
          },
          {
            path: 'general-rules',
            loadComponent: () =>
              import(
                './base/static-pages/general-rules/general-rules.component'
              ).then(c => c.GeneralRulesComponent),
          },
        ],
      },
      {
        path: 'foundations',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: 'accessibility',
            pathMatch: 'full',
          },
          {
            path: 'accessibility',
            loadComponent: () =>
              import(
                './base/static-pages/accessibility/accessibility.component'
              ).then(c => c.AccessibilityComponent),
          },
          {
            path: 'colors',
            loadComponent: () =>
              import('./base/static-pages/colors/colors.component').then(
                c => c.ColorsComponent,
              ),
          },
          {
            path: 'copywriting',
            loadComponent: () =>
              import(
                './base/static-pages/copywriting/copywriting.component'
              ).then(c => c.CopywritingComponent),
          },
          {
            path: 'error-handling',
            loadComponent: () =>
              import(
                './base/static-pages/error-handling/error-handling.component'
              ).then(c => c.ErrorHandlingComponent),
          },
          {
            path: 'grid',
            loadComponent: () =>
              import('./base/static-pages/grid/grid.component').then(
                c => c.GridComponent,
              ),
          },
          {
            path: 'illustrations',
            loadComponent: () =>
              import(
                './base/static-pages/illustrations/illustrations.component'
              ).then(c => c.IllustrationsComponent),
          },
          {
            path: 'layouts',
            loadComponent: () =>
              import('./base/static-pages/layouts/layouts.component').then(
                c => c.LayoutsComponent,
              ),
          },
          {
            path: 'spacing',
            loadComponent: () =>
              import('./base/static-pages/spacing/spacing.component').then(
                c => c.SpacingComponent,
              ),
          },
          {
            path: 'typography',
            loadComponent: () =>
              import(
                './base/static-pages/typography/typography.component'
              ).then(c => c.TypographyComponent),
          },
          {
            path: 'forms',
            loadComponent: () =>
              import('./base/static-pages/forms/forms.component').then(
                c => c.FormsComponent,
              ),
          },
        ],
      },
      {
        path: 'components',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: componentRoutes[0].path,
            pathMatch: 'full',
          },
          ...componentRoutes,
        ],
      },
      {
        path: 'patterns',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: () =>
              inject(ENVIRONMENT_NAME) === 'prod'
                ? (patternRoutes[0].path ?? '')
                : 'all',
            pathMatch: 'full',
          },
          {
            path: 'all',
            loadComponent: () =>
              import('./patterns-overview/patterns-overview.component').then(
                c => c.PatternsOverviewComponent,
              ),
            canActivate: [BitsGuard],
          },
          ...patternRoutes,
        ],
      },
      {
        path: 'directives',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: directiveRoutes[0].path,
            pathMatch: 'full',
          },
          ...directiveRoutes,
        ],
      },
      {
        path: 'pipes',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: pipeRoutes[0].path,
            pathMatch: 'full',
          },
          ...pipeRoutes,
        ],
      },
      {
        path: 'motion',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: 'getting-started',
            pathMatch: 'full',
          },
          {
            path: 'getting-started',
            loadComponent: () =>
              import(
                './base/static-pages/motion-system/getting-started.component'
              ).then(c => c.GettingStartedComponent),
          },
          {
            path: 'attention-seeker',
            loadComponent: () =>
              import(
                './base/static-pages/motion-system/attention-seeker/attention-seeker.component'
              ).then(c => c.AttentionSeekerComponent),
          },
          {
            path: 'fade',
            loadComponent: () =>
              import(
                './base/static-pages/motion-system/fade/fade.component'
              ).then(c => c.FadeComponent),
          },
          {
            path: 'slide',
            loadComponent: () =>
              import(
                './base/static-pages/motion-system/slide/slide.component'
              ).then(c => c.SlideComponent),
          },
          {
            path: 'grow',
            loadComponent: () =>
              import(
                './base/static-pages/motion-system/grow/grow.component'
              ).then(c => c.GrowComponent),
          },
        ],
      },
      {
        path: 'brand',
        component: BaseComponent,
        children: [
          {
            path: '',
            redirectTo: 'brand-style-guide',
            pathMatch: 'full',
          },
          {
            path: 'brand-style-guide',
            loadComponent: () =>
              import(
                './base/static-pages/brand/brand-style-guide.component'
              ).then(c => c.BrandStyleGuideComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'skits',
    loadChildren: () =>
      import('bits/skits/skits.module').then(m => m.SkitsModule),
  },
  {
    path: 'internal/blank',
    component: EmptyComponent,
  },
  {
    path: '',
    children: [
      {
        path: 'internal/404',
        component: NotFoundComponent,
      },
      {
        path: 'internal',
        loadChildren: () =>
          import('bits/internal/internal.module').then(m => m.InternalModule),
      },
      {
        path: '**',
        component: NotFoundComponent,
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'enabledBlocking',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      urlUpdateStrategy: 'eager',
    }),
  ],
  providers: [],
  exports: [RouterModule],
})
export class AppRoutingModule {}
